import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';
import { Globe, CreditCard, Mail, Save } from 'lucide-react';

const TABS = [
  { key: 'website', label: 'Website', icon: Globe },
  { key: 'payment', label: 'Payment Gateway', icon: CreditCard },
  { key: 'email-sms', label: 'Email / SMS', icon: Mail },
];

export default function AdminGeneralSettings() {
  const [tab, setTab] = useState('website');
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get('/settings/general')
      .then(r => setForm(r.data))
      .catch(err => toast.error(err.response?.data?.message || 'Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const update = (path, value) => {
    setForm(prev => {
      const next = { ...prev };
      if (path.includes('.')) {
        const [parent, child] = path.split('.');
        next[parent] = { ...next[parent], [child]: value };
      } else {
        next[path] = value;
      }
      return next;
    });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.put('/settings/general', form);
      toast.success(res.data.message);
      setForm(res.data.settings);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return <DashboardLayout><div className="loader-wrap"><div className="loader"></div></div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <h1 className="page-title">Website & Integration Settings</h1>
      <p className="page-subtitle">Site branding, payment gateway, and email/SMS provider configuration</p>

      <div className="tab-bar" style={{ marginBottom: 16 }}>
        {TABS.map(t => (
          <button key={t.key} className={`tab-btn ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      <form onSubmit={save} className="card">
        {tab === 'website' && (
          <>
            <div className="grid-2" style={{ gap: 16 }}>
              <div className="form-group">
                <label>Site Name</label>
                <input type="text" className="form-control" value={form.siteName} onChange={e => update('siteName', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Logo URL</label>
                <input type="text" className="form-control" value={form.siteLogoUrl} onChange={e => update('siteLogoUrl', e.target.value)} placeholder="https://..." />
              </div>
              <div className="form-group">
                <label>Support Email</label>
                <input type="email" className="form-control" value={form.supportEmail} onChange={e => update('supportEmail', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Support Phone</label>
                <input type="text" className="form-control" value={form.supportPhone} onChange={e => update('supportPhone', e.target.value)} />
              </div>
              <div className="form-group">
                <label>WhatsApp Number</label>
                <input type="text" className="form-control" value={form.whatsappNumber} onChange={e => update('whatsappNumber', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Telegram Link</label>
                <input type="text" className="form-control" value={form.telegramLink} onChange={e => update('telegramLink', e.target.value)} placeholder="https://t.me/..." />
              </div>
            </div>
            <div className="form-group">
              <label>Address</label>
              <textarea className="form-control" rows={2} value={form.address} onChange={e => update('address', e.target.value)} />
            </div>
          </>
        )}

        {tab === 'payment' && (
          <>
            <div className="alert alert-info" style={{ marginBottom: 16 }}>
              These settings store gateway config only — actual payment API integration is a separate development step. Until an automated gateway is wired up, keep provider set to "Manual" so users pay via UPI/QR and admin approves manually (as already implemented in Package Management).
            </div>
            <div className="grid-2" style={{ gap: 16 }}>
              <div className="form-group">
                <label>Provider</label>
                <select className="form-control" value={form.paymentGateway.provider} onChange={e => update('paymentGateway.provider', e.target.value)}>
                  <option value="manual">Manual (UPI/QR + Admin Approval)</option>
                  <option value="razorpay">Razorpay</option>
                  <option value="cashfree">Cashfree</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={form.paymentGateway.enabled} onChange={e => update('paymentGateway.enabled', e.target.checked)} />
                  Enable Automated Gateway
                </label>
              </div>
            </div>

            {form.paymentGateway.provider === 'manual' ? (
              <div className="grid-2" style={{ gap: 16 }}>
                <div className="form-group">
                  <label>Manual UPI ID</label>
                  <input type="text" className="form-control" value={form.paymentGateway.manualUpiId} onChange={e => update('paymentGateway.manualUpiId', e.target.value)} placeholder="trivenipay@upi" />
                </div>
                <div className="form-group">
                  <label>QR Code Image URL</label>
                  <input type="text" className="form-control" value={form.paymentGateway.manualQrImageUrl} onChange={e => update('paymentGateway.manualQrImageUrl', e.target.value)} placeholder="https://..." />
                </div>
              </div>
            ) : (
              <>
                <div className="grid-2" style={{ gap: 16 }}>
                  <div className="form-group">
                    <label>Merchant ID</label>
                    <input type="text" className="form-control" value={form.paymentGateway.merchantId} onChange={e => update('paymentGateway.merchantId', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Mode</label>
                    <select className="form-control" value={form.paymentGateway.mode} onChange={e => update('paymentGateway.mode', e.target.value)}>
                      <option value="test">Test</option>
                      <option value="live">Live</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>API Key</label>
                    <input type="text" className="form-control" value={form.paymentGateway.apiKey} onChange={e => update('paymentGateway.apiKey', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>API Secret</label>
                    <input type="password" className="form-control" value={form.paymentGateway.apiSecret} onChange={e => update('paymentGateway.apiSecret', e.target.value)} />
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {tab === 'email-sms' && (
          <>
            <h3 className="card-title" style={{ marginBottom: 12 }}>Email (SMTP)</h3>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={form.email.enabled} onChange={e => update('email.enabled', e.target.checked)} />
                Enable Email Notifications
              </label>
            </div>
            <div className="grid-2" style={{ gap: 16 }}>
              <div className="form-group">
                <label>SMTP Host</label>
                <input type="text" className="form-control" value={form.email.smtpHost} onChange={e => update('email.smtpHost', e.target.value)} placeholder="smtp.gmail.com" />
              </div>
              <div className="form-group">
                <label>SMTP Port</label>
                <input type="number" className="form-control" value={form.email.smtpPort} onChange={e => update('email.smtpPort', Number(e.target.value))} />
              </div>
              <div className="form-group">
                <label>SMTP User</label>
                <input type="text" className="form-control" value={form.email.smtpUser} onChange={e => update('email.smtpUser', e.target.value)} />
              </div>
              <div className="form-group">
                <label>SMTP Password</label>
                <input type="password" className="form-control" value={form.email.smtpPass} onChange={e => update('email.smtpPass', e.target.value)} />
              </div>
              <div className="form-group">
                <label>From Email</label>
                <input type="email" className="form-control" value={form.email.fromEmail} onChange={e => update('email.fromEmail', e.target.value)} />
              </div>
            </div>

            <h3 className="card-title" style={{ margin: '20px 0 12px' }}>SMS</h3>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={form.sms.enabled} onChange={e => update('sms.enabled', e.target.checked)} />
                Enable SMS Notifications
              </label>
            </div>
            <div className="grid-2" style={{ gap: 16 }}>
              <div className="form-group">
                <label>Provider</label>
                <select className="form-control" value={form.sms.provider} onChange={e => update('sms.provider', e.target.value)}>
                  <option value="msg91">MSG91</option>
                  <option value="twilio">Twilio</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Sender ID</label>
                <input type="text" className="form-control" value={form.sms.senderId} onChange={e => update('sms.senderId', e.target.value)} />
              </div>
              <div className="form-group">
                <label>API Key</label>
                <input type="password" className="form-control" value={form.sms.apiKey} onChange={e => update('sms.apiKey', e.target.value)} />
              </div>
            </div>
          </>
        )}

        <button type="submit" className="btn btn-primary" disabled={saving}>
          <Save size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </form>
    </DashboardLayout>
  );
}